import { escapeHtml, cleanName, formatClock, formatMinutes, qualityOf, debounce, normalizeText, tmdbSized } from '../core/utils.js';
import { epg, programProgress } from '../core/epg.js';
import { catalog } from '../core/catalog.js';
import { t, i18n } from '../core/i18n.js';
import { scheduleCardTrailer, cancelCardTrailer } from './card-trailer.js';
import { device } from '../core/device.js';
import { settings } from '../core/storage.js';
import { firstGenre } from '../core/genres.js';
import { stripCountry } from '../core/country.js';
import { posterFill } from '../core/poster-fill.js';

/** Título para enseñar: sin el prefijo de país del proveedor («FR| La Joconde» → «La Joconde»). */
export const titleOf = (item) => stripCountry(item && item.name);

/** Año, país y género de una tarjeta: «2026 · FR · Drama». */
const cardSub = (item) => [item.year, item.region, firstGenre(item.genre)].filter(Boolean).join(' · ');

/* Helpers de renderizado compartidos por todas las páginas (HTML como string + delegación de eventos).
   Atributos de acción reconocidos por el manejador global (main.js):
     data-play-live="id"    reproduce un canal
     data-open="vod|series:id"  abre la ficha
     data-fav="kind:id"     alterna Mi lista
     data-href="/ruta/"     navega */

/** URL con la que cargar una imagen remota. En la web pasa por la caché del servidor (/image):
    evita el bloqueo de contenido mixto (http en página https) y sirve logos y carátulas desde
    disco en lugar de pedirlos al panel cada vez. En apps nativas se carga directa. */
// Leer `innerWidth` obliga al navegador a recalcular la página; pintar 300 tarjetas lo hacía 300 veces (medio segundo en un
// móvil). Se calcula una vez y se renueva solo al girar o redimensionar.
let narrowCache = null;
const narrowScreen = () => {
    if (narrowCache === null) narrowCache = Math.min(window.screen ? window.screen.width : 9999, window.innerWidth || 9999) <= 900;
    return narrowCache;
};
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') ['resize', 'orientationchange'].forEach((ev) => window.addEventListener(ev, () => { narrowCache = null; }));

export function imageUrl(src, opts = {}) {
    if (!src) return '';
    src = tmdbSized(src, Object.assign({ narrow: narrowScreen() }, opts));
    if (device.isCordova || device.isElectron || !/^https?:$/.test(location.protocol)) return src;
    if (!/^https?:\/\//i.test(src) || src.indexOf(location.origin + '/') === 0) return src;
    return location.origin + '/image?url=' + encodeURIComponent(src);
}

export function img(src, alt = '') {
    if (!src) return '';
    return `<img data-src="${escapeHtml(imageUrl(src, { card: true }))}" alt="${escapeHtml(alt)}" loading="lazy" decoding="async"/>`;
}

/** `reach`: en la tele el botón de una fila (canal, lista de películas) se alcanza con la flecha derecha desde la fila. */
export function favButton(kind, id, { reach = false } = {}) {
    const active = catalog.isFavorite(kind, id);
    return `<button class="card-fav ${active ? 'is-active' : ''}" data-fav="${kind}:${id}" aria-label="${active ? t('common.favoriteRemove') : t('common.favoriteAdd')}"${reach && device.isTV ? '' : ' tabindex="-1"'}><span class="mi">${active ? 'check' : 'add'}</span></button>`;
}

/** Valoración como pastilla «★ 7,5» (vacía si no hay dato). */
// `toLocaleString` con opciones crea un formateador nuevo cada vez (123 ms por pantalla de inicio); se reutiliza uno.
let ratingFmt = null;
const ratingFormat = () => ratingFmt || (ratingFmt = new Intl.NumberFormat(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }));

/** Año, valoración, edad y género de un programa de la guía (ver core/epg-desc.js) con las mismas etiquetas que las fichas; '' si no hay. */
export function programMeta(p) {
    if (!p || !p.hasMeta) return '';
    const meta = [];
    if (p.year) meta.push(`<span>${escapeHtml(p.year)}</span>`);
    if (p.rating) meta.push(ratingPill(p.rating) + (p.votes ? `<span class="prog-votes">(${escapeHtml(new Intl.NumberFormat(i18n.lang).format(p.votes))})</span>` : ''));
    if (p.age) meta.push(`<span class="tag tag-age">${escapeHtml(p.age)}</span>`);
    if (p.genres.length) meta.push(`<span>${escapeHtml(p.genres.slice(0, 2).join(', '))}</span>`);
    if (p.country) meta.push(`<span class="prog-country">${escapeHtml(p.country)}</span>`);
    return meta.length ? `<div class="prog-meta">${meta.join('')}</div>` : '';
}

export function ratingPill(rating) {
    const n = Number(rating);
    if (!n) return '';
    return `<span class="card-rating"><span class="mi">star</span>${ratingFormat().format(n)}</span>`;
}

/** Fila de película/serie para la vista de lista. */
export function mediaRow(item, kind) {
    const p = kind === 'vod' ? catalog.getProgress('vod', item.id) : catalog.seriesProgress(item.id);
    const sub = kind === 'series' && p ? `T${p.season} E${p.episode}` : cardSub(item);
    return `<a class="mitem" href="#" data-open="${kind}:${item.id}" aria-label="${escapeHtml(titleOf(item))}">
        <span class="mitem-poster"${item.poster ? '' : ` data-poster-need="${kind}:${item.id}"`}>${img(item.poster, titleOf(item))}<span class="mi">${kind === 'vod' ? 'movie' : 'tv'}</span></span>
        <span class="mitem-body">
            <span class="mitem-title">${escapeHtml(titleOf(item))}</span>
            ${sub ? `<span class="mitem-sub">${escapeHtml(sub)}</span>` : ''}
            ${p && p.pct > 0 && !p.finished ? `<span class="chitem-bar"><i style="width:${p.pct}%"></i></span>` : ''}
        </span>
        ${ratingPill(item.rating)}
        <span class="chitem-actions">${favButton(kind, item.id, { reach: true }).replace('card-fav', 'btn btn-icon card-fav-inline')}</span>
    </a>`;
}

/** Tarjeta de una colección de Jellyfin, Emby o Plex (se abre con data-collection, ver main.js). */
export function collectionCard(col) {
    return `<a class="card" href="#" data-collection="${escapeHtml(col.key)}" aria-label="${escapeHtml(col.name)}">
        <div class="card-poster">
            <span class="card-fallback"><span class="mi">collections_bookmark</span></span>
            ${img(col.poster, col.name)}
        </div>
        <div class="card-body card-body-media">
            <div class="card-title">${escapeHtml(col.name)}</div>
            <div class="card-meta-row"><span class="card-sub">${escapeHtml(t('home.collectionCount', { n: col.items.length }))}</span></div>
        </div>
    </a>`;
}

/* Filas por categorías (Películas y Series): la tarjeta con el foco (mando) o con el ratón encima se despliega en horizontal, con su
   fondo, título, botones (Reproducir, Mi lista, Información) y datos, como en Prime Video. El resto sigue siendo carátula. El contenido
   extra (fondo, edad, duración) se pide al abrir una ficha y solo cuando la tarjeta se queda desplegada un momento. Con el mando, OK abre
   la ficha y mantener OK abre el menú; los botones son para el ratón (en la tele no se enfocan: «tabindex -1»). */
function expandHtml(kind, item) {
    const id = item.id;
    const active = catalog.isFavorite(kind, id);
    const sub = cardSub(item);
    return `<div class="card-expand" data-expand="${kind}:${id}"><div class="ce-art"></div><div class="ce-body">
        <div class="ce-title">${escapeHtml(titleOf(item))}</div>
        <div class="ce-actions">
            <button type="button" class="ce-play" tabindex="-1" data-play="${kind}:${id}"><span class="mi">play_arrow</span>${escapeHtml(t('common.play'))}</button>
            <button type="button" class="ce-btn" tabindex="-1" data-fav="${kind}:${id}" aria-label="${escapeHtml(active ? t('common.favoriteRemove') : t('common.favoriteAdd'))}"><span class="mi">${active ? 'check' : 'add'}</span></button>
            <button type="button" class="ce-btn" tabindex="-1" data-open="${kind}:${id}" aria-label="${escapeHtml(t('common.info'))}"><span class="mi">info_outline</span></button>
        </div>
        <div class="ce-meta" data-ce-meta>${ratingPill(item.rating)}${sub ? `<span>${escapeHtml(sub)}</span>` : ''}</div>
    </div></div>`;
}

const expandTimers = new WeakMap();

/** Completa la tarjeta desplegada con la ficha (fondo, edad, duración). Una vez por tarjeta. */
async function fillExpand(card) {
    if (card.hasAttribute('data-ce-done')) return;
    card.setAttribute('data-ce-done', '');
    const [kind, id] = String(card.getAttribute('data-open') || '').split(':');
    const art = card.querySelector('.ce-art');
    const meta = card.querySelector('[data-ce-meta]');
    if (!art || !id) return;
    const item = catalog.get(kind, id);
    if (item && item.poster) art.style.backgroundImage = `url('${imageUrl(item.poster, { card: true })}')`; // mientras llega el fondo de la ficha
    let info = null;
    try { info = kind === 'vod' ? await catalog.vodInfo(id) : await catalog.seriesInfo(id); } catch (e) { info = null; }
    if (!info || !card.isConnected) return;
    const back = info.backdrop || (Array.isArray(info.backdrops) && info.backdrops[0]) || '';
    if (back) { art.style.backgroundImage = `url('${imageUrl(back, { size: 'w780' })}')`; art.classList.add('is-backdrop'); }
    if (meta) {
        const bits = [];
        if (info.age) bits.push(`<span class="ce-age">${escapeHtml(String(info.age))}</span>`);
        const genre = firstGenre(info.genre || (item && item.genre));
        if (genre) bits.push(`<span>${escapeHtml(genre)}</span>`);
        const dur = kind === 'vod' && info.durationSecs ? formatMinutes(info.durationSecs) : kind === 'series' && info.seasons && info.seasons.length ? t('share.seasons', { n: info.seasons.length }) : '';
        if (dur) bits.push(`<span>${escapeHtml(dur)}</span>`);
        meta.innerHTML = ratingPill(info.rating || (item && item.rating)) + bits.join('');
    }
    card._ceInfo = info; // el tráiler se pide cada vez que la tarjeta vuelve a abrirse
    scheduleCardTrailer(card, info);
}

/** Engancha la tarjeta desplegable de las filas por categorías: se rellena tras un instante con el foco o el ratón, y se vuelve a centrar al terminar de ensancharse. */
export function bindExpandCards(root) {
    const schedule = (e) => {
        const card = e.target.closest && e.target.closest('.card.has-expand');
        if (card && card.hasAttribute('data-ce-done') && card._ceInfo) { scheduleCardTrailer(card, card._ceInfo); return; }
        if (!card || card.hasAttribute('data-ce-done')) return;
        clearTimeout(expandTimers.get(card));
        expandTimers.set(card, setTimeout(() => fillExpand(card), 350));
    };
    const cancel = (e) => {
        const card = e.target.closest && e.target.closest('.card.has-expand');
        if (!card) return;
        clearTimeout(expandTimers.get(card));
        if (!(e.relatedTarget && card.contains(e.relatedTarget))) cancelCardTrailer(card); // pasar de un botón a otro dentro de la tarjeta no la cierra
    };
    root.addEventListener('focusin', schedule);
    root.addEventListener('mouseover', schedule);
    root.addEventListener('focusout', cancel);
    root.addEventListener('mouseout', cancel);
    // La tarjeta crece (animación): con el mando, al acabar se vuelve a dejar centrada en la fila.
    root.addEventListener('transitionend', (e) => {
        if (e.propertyName !== 'width') return;
        const card = e.target;
        if (card.classList && card.classList.contains('is-focused')) card.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
    });
}

export function movieCard(item, { progress, expand = false } = {}) {
    const p = progress || catalog.getProgress('vod', item.id);
    return `<a class="card${expand ? ' has-expand' : ''}" href="#" data-open="vod:${item.id}" aria-label="${escapeHtml(titleOf(item))}">
        <div class="card-poster ${item.logoArt ? 'is-logo' : ''}"${item.poster ? '' : ` data-poster-need="vod:${item.id}"`}>
            <span class="card-fallback"><span class="mi">movie</span></span>
            ${img(item.poster, titleOf(item))}
            ${favButton('vod', item.id)}
            ${p && p.finished ? `<span class="card-watched" title="${t('watched.badge')}"><span class="mi">check</span></span>` : ''}
            ${p && p.pct > 0 && !p.finished ? `<div class="card-progress"><i style="width:${p.pct}%"></i></div>` : ''}
        </div>
        <div class="card-body card-body-media">
            <div class="card-title">${escapeHtml(titleOf(item))}</div>
            <div class="card-meta-row"><span class="card-sub">${escapeHtml(cardSub(item))}</span>${ratingPill(item.rating)}</div>
        </div>
        ${expand ? expandHtml('vod', item) : ''}
    </a>`;
}

export function seriesCard(item, { progress, expand = false } = {}) {
    const p = progress || catalog.seriesProgress(item.id);
    return `<a class="card${expand ? ' has-expand' : ''}" href="#" data-open="series:${item.id}" aria-label="${escapeHtml(titleOf(item))}">
        <div class="card-poster ${item.logoArt ? 'is-logo' : ''}"${item.poster ? '' : ` data-poster-need="series:${item.id}"`}>
            <span class="card-fallback"><span class="mi">tv</span></span>
            ${img(item.poster, titleOf(item))}
            ${favButton('series', item.id)}
            ${catalog.isWatched('seriesdone', item.id) ? `<span class="card-watched" title="${t('watched.badge')}"><span class="mi">check</span></span>` : ''}
            ${p && !p.finished ? `<div class="card-progress"><i style="width:${p.pct}%"></i></div>` : ''}
        </div>
        <div class="card-body card-body-media">
            <div class="card-title">${escapeHtml(titleOf(item))}</div>
            <div class="card-meta-row"><span class="card-sub">${p ? `T${p.season} E${p.episode}` : escapeHtml(cardSub(item))}</span>${ratingPill(item.rating)}</div>
        </div>
        ${expand ? expandHtml('series', item) : ''}
    </a>`;
}

export function channelCard(ch, { wide = false } = {}) {
    const nn = epg.nowNext(ch.epgId);
    const quality = qualityOf(ch.name);
    const compact = wide && settings.get('compactLiveRow') !== false;
    return `<a class="card card-channel${wide ? ' card-wide card-continue' : ''}${compact ? ' is-compact' : ''}" href="#" data-play-live="${ch.id}" aria-label="${escapeHtml(cleanName(ch.name))}">
        <div class="card-poster">
            ${img(ch.logo, '')}
            ${quality ? `<span class="card-badge">${quality}</span>` : ''}
            ${wide ? `<span class="card-badge is-right is-live">${t('common.live')}</span>` : ch.archive ? `<span class="card-badge is-right" title="Catch-up">⟲</span>` : ''}
            ${favButton('live', ch.id)}
            ${nn.now ? `<span class="card-chname">${escapeHtml(cleanName(ch.name))}</span>` : ''}
        </div>
        <div class="card-body">
            ${nn.now
                ? `<div class="card-title">${escapeHtml(nn.now.title)}</div><div class="card-sub">${formatClock(nn.now.start)} - ${formatClock(nn.now.stop)}</div><div class="card-epg-bar"><i style="width:${programProgress(nn.now)}%"></i></div>`
                : `<div class="card-title">${escapeHtml(cleanName(ch.name))}</div><div class="card-sub">${escapeHtml((catalog.category('live', ch.categoryId) || {}).name || '')}</div>`}
        </div>
    </a>`;
}

export function channelRow(ch, { current } = {}) {
    const nn = epg.nowNext(ch.epgId);
    return `<a class="chitem ${current ? 'is-current' : ''}" href="#" data-play-live="${ch.id}">
        <span class="chitem-num">${ch.num}</span>
        <span class="chitem-logo ${ch.logo ? '' : 'is-empty'}">${ch.logo ? img(ch.logo, '') : ''}</span>
        <span class="chitem-body">
            <span class="chitem-name">${escapeHtml(cleanName(ch.name))}</span>
            ${nn.now
                ? `<span class="chitem-epg">${escapeHtml(formatClock(nn.now.start))} · ${escapeHtml(nn.now.title)}</span><span class="chitem-bar"><i style="width:${programProgress(nn.now)}%"></i></span>`
                : `<span class="chitem-epg">${escapeHtml((catalog.category('live', ch.categoryId) || {}).name || '')}</span>`}
        </span>
        <span class="chitem-actions">${favButton('live', ch.id, { reach: true }).replace('card-fav', 'btn btn-icon card-fav-inline')}</span>
    </a>`;
}

export { firstGenre };

/** Tarjeta de "Continuar viendo": imagen con el % visto, título, datos y menú ⋮ (data-card-menu). */
export function continueCard(row) {
    const p = row.progress;
    const item = row.item;
    const kind = row.kind === 'vod' ? 'vod' : 'series';
    const info = catalog.peekInfo(kind, item.id) || item;
    const meta = kind === 'vod'
        ? [item.year, firstGenre(item.genre || info.genre), formatMinutes(p.duration || info.durationSecs)]
        : [`T${p.season} E${p.episode}`, p.title && p.title !== item.name ? p.title : firstGenre(item.genre)];
    return `<a class="card card-wide card-continue" href="#" data-open="${kind}:${item.id}" aria-label="${escapeHtml(titleOf(item))}">
        <div class="card-poster">
            <span class="card-fallback"><span class="mi">${kind === 'vod' ? 'movie' : 'tv'}</span></span>
            ${img(info.backdrop || item.backdrop || item.poster, item.name)}
            <span class="card-badge">${Math.max(1, Math.round(p.pct))}% ${t('common.watched')}</span>
            <div class="card-progress"><i style="width:${p.pct}%"></i></div>
        </div>
        <div class="card-body card-body-more">
            <div class="card-text">
                <div class="card-title">${escapeHtml(titleOf(item))}</div>
                <div class="card-sub">${escapeHtml(meta.filter(Boolean).join(' · '))}</div>
            </div>
            <button class="card-more" data-card-menu="${kind}:${item.id}" aria-label="${t('common.moreOptions')}" tabindex="-1"><span class="mi">more_vert</span></button>
        </div>
    </a>`;
}

/** Envuelve una tarjeta con su puesto del «Top 10»: el número grande en contorno queda detrás, a la izquierda. */
export function topItem(rank, cardHtml) {
    return `<div class="top-item${rank >= 10 ? ' is-wide-num' : ''}"><span class="top-num" aria-hidden="true">${rank}</span>${cardHtml}</div>`;
}

export function row({ id, title, items, seeAll, seeAllAction, kind }) {
    if (!items || !items.length) return '';
    return `<section class="section" data-row-id="${escapeHtml(id)}">
        <div class="section-head">
            <h2 class="section-title">${escapeHtml(title)}</h2>
            ${seeAll ? `<a class="section-link" href="#" data-href="${escapeHtml(seeAll)}">${t('common.seeAll')} <span class="mi">chevron_right</span></a>` : ''}
            ${seeAllAction ? `<a class="section-link" href="#" data-cat-all="${escapeHtml(seeAllAction)}">${t('common.seeAll')} <span class="mi">chevron_right</span></a>` : ''}
        </div>
        <div class="row">
            <button class="row-arrow is-left" aria-hidden="true" tabindex="-1"><span class="mi">chevron_left</span></button>
            <div class="row-scroller" data-nav-row data-kind="${kind || ''}">${items.join('')}</div>
            <button class="row-arrow is-right" aria-hidden="true" tabindex="-1"><span class="mi">chevron_right</span></button>
        </div>
    </section>`;
}

/**
 * Agrupa los títulos por categoría, en el orden de `categories` ([{ id, name }]); lo que no encaja va al final.
 * Con `watchedAt` (título → marca de tiempo, 0 si no se ha visto) cada categoría empieza por lo visto más recientemente
 * y las categorías con lo visto más reciente van primero; el resto conserva su orden.
 */
export function groupByCategory(items, categories, watchedAt) {
    const byId = new Map();
    items.forEach((it) => {
        const id = String(it.categoryId == null ? '' : it.categoryId);
        if (!byId.has(id)) byId.set(id, []);
        byId.get(id).push(it);
    });
    const groups = [];
    (categories || []).forEach((c) => {
        const list = byId.get(String(c.id));
        if (list && list.length) { groups.push({ id: String(c.id), name: c.name, items: list }); byId.delete(String(c.id)); }
    });
    byId.forEach((list, id) => groups.push({ id, name: t('common.uncategorized'), items: list }));
    if (watchedAt) {
        groups.forEach((g) => {
            const at = new Map(g.items.map((it) => [it, watchedAt(it)]));
            g.lastSeen = Math.max(0, ...at.values());
            if (!g.lastSeen) return;
            const seen = g.items.filter((it) => at.get(it) > 0).sort((a, b) => at.get(b) - at.get(a));
            g.items = seen.concat(g.items.filter((it) => !(at.get(it) > 0)));
        });
        // Las categorías con algo visto, primero y de más a menos reciente (orden estable: el resto, como estaba).
        const index = new Map(groups.map((g, i) => [g, i]));
        groups.sort((a, b) => (b.lastSeen > 0) - (a.lastSeen > 0) || b.lastSeen - a.lastSeen || index.get(a) - index.get(b));
    }
    return groups;
}

/** Lo visto más recientemente primero (según `watchedAt`); lo que nunca se ha visto, detrás y en su orden. */
export function sortByWatched(items, watchedAt) {
    const at = new Map(items.map((it) => [it, watchedAt(it)]));
    return items.filter((it) => at.get(it) > 0).sort((a, b) => at.get(b) - at.get(a)).concat(items.filter((it) => !(at.get(it) > 0)));
}

/** Tarjetas pendientes de cada fila por categoría, por su id de fila: { items, cardFor, shown, step }. */
const rowFeeds = new Map();
const MORE_BEFORE_END_PX = 900;

/** Siguiente tanda de tarjetas de una fila (HTML) y avanza lo mostrado; '' si ya no queda nada. */
export function takeMoreCards(feed) {
    if (!feed || feed.shown >= feed.items.length) return '';
    const next = feed.items.slice(feed.shown, feed.shown + feed.step);
    feed.shown += next.length;
    return next.map(feed.cardFor).join('');
}

/** Filas horizontales (una por categoría) de los grupos desde `from`, `count` de ellas, con «Ver todo» a esa categoría. */
export function categorySections(groups, { from = 0, count = 5, cardFor, kind = '', perRow = 20 } = {}) {
    // Una categoría con más de `perRow` títulos sigue cargando hacia la derecha según se desliza la fila (ver bindRowArrows).
    groups.slice(from, from + count).forEach((g) => { if (g.items.length > perRow) rowFeeds.set('cat-' + g.id, { items: g.items, cardFor, shown: perRow, step: perRow }); });
    return groups.slice(from, from + count).map((g) => row({ id: 'cat-' + g.id, title: stripCountry(g.name), items: g.items.slice(0, perRow).map(cardFor), seeAllAction: g.items.length > perRow ? g.id : '', kind })).join('');
}

export function skeletonRow(kind = 'poster', count = 8) {
    const cls = kind === 'wide' ? 'is-wide' : kind === 'channel' ? 'is-channel' : '';
    const cards = Array.from({ length: count }, () => `<div class="skeleton-card ${cls}"><div class="skeleton skeleton-poster"></div><div class="skeleton skeleton-line"></div></div>`).join('');
    return `<section class="section"><div class="section-head"><div class="skeleton" style="width:180px;height:20px"></div></div><div class="row-scroller">${cards}</div></section>`;
}

export function skeletonGrid(kind = 'poster', count = 18) {
    const cls = kind === 'wide' ? 'is-wide' : kind === 'channel' ? 'is-channel' : '';
    return `<div class="grid ${kind === 'channel' ? 'grid-channels' : ''}">${Array.from({ length: count }, () => `<div class="skeleton-card ${cls}" style="width:auto"><div class="skeleton skeleton-poster"></div><div class="skeleton skeleton-line"></div></div>`).join('')}</div>`;
}

export function empty({ icon = 'search_off', title, text, action }) {
    return `<div class="empty"><span class="mi">${icon}</span>${title ? `<h3>${escapeHtml(title)}</h3>` : ''}${text ? `<p>${escapeHtml(text)}</p>` : ''}${action || ''}</div>`;
}

export function chips(list, active, { attr = 'data-chip', allLabel } = {}) {
    const all = allLabel === false ? '' : `<button class="chip ${!active || active === 'all' ? 'is-active' : ''}" ${attr}="all">${allLabel || t('common.all')}</button>`;
    return `<div class="chips" data-nav-row>${all}${list.map((c) => `<button class="chip ${active === c.id ? 'is-active' : ''}" ${attr}="${escapeHtml(c.id)}">${c.icon ? `<span class="mi" style="font-size:16px">${c.icon}</span>` : ''}${escapeHtml(c.name)}${c.count != null ? `<span class="chip-count">${c.count}</span>` : ''}</button>`).join('')}</div>`;
}

/* ---------- Barra de filtros (menús desplegables) ---------- */
/**
 * filters: [{ key, icon, label, value }] → botón por filtro que abre un selector (openCategoryPicker).
 * Sustituye a las filas de chips con scroll: con paneles reales (decenas de categorías) no escalan
 * y en TV/móvil un menú es más cómodo.
 */
export function filterBar(filters) {
    return `<div class="filters" data-nav-row data-nav-reach>${filters.map((f) => `<button class="filter-btn ${f.active ? 'is-active' : ''}" data-filter="${escapeHtml(f.key)}" aria-label="${escapeHtml(f.label)}">
        <span class="mi">${f.icon}</span>
        <span class="filter-text"><span class="filter-label">${escapeHtml(f.label)}</span><span class="filter-value">${escapeHtml(f.value)}</span></span>
        <span class="filter-compact">${escapeHtml(f.active || f.compactValue ? f.value : f.label)}</span>
        <span class="mi filter-arrow">expand_more</span>
    </button>`).join('')}</div>`;
}

/* ---------- Selector en popup (listado completo + búsqueda) ---------- */
/* Con paneles reales que traen decenas de categorías hace falta un buscador que las liste
   todas de un vistazo. Para listas cortas (calidad, orden) el buscador se oculta. */

export function categoryPickerItems(list, active, multi) {
    const selected = Array.isArray(active) ? active.map(String) : null;
    let firstActive = true;
    let lastGroup = '';
    return list.map((c) => {
        // Con `group` (p. ej. el tipo de proveedor) se pone un título cada vez que cambia; sin él, tras un grupo, una línea de separación.
        const group = c.group || '';
        let header = '';
        if (group !== lastGroup) header = group ? `<div class="catpick-group" role="presentation">${escapeHtml(group)}</div>` : '<div class="catpick-group is-plain" role="presentation"></div>';
        lastGroup = group;
        const id = String(c.id);
        const isActive = selected ? (selected.length ? selected.indexOf(id) !== -1 : id === 'all') : (active === c.id || (!active && id === 'all'));
        const navDefault = isActive && firstActive;
        if (isActive) firstActive = false;
        const check = multi && !c.exclusive
            ? `<span class="mi catpick-check ${isActive ? '' : 'is-off'}">${isActive ? 'check_box' : 'check_box_outline_blank'}</span>`
            : (isActive ? '<span class="mi catpick-check">check</span>' : '');
        return `${header}<button class="catpick-item ${isActive ? 'is-active' : ''}" data-catpick="${escapeHtml(id)}" ${navDefault ? 'data-nav-default' : ''}>
            <span class="mi">${c.icon || 'label'}</span>
            <span class="catpick-name">${escapeHtml(c.name)}</span>
            ${c.count != null ? `<span class="catpick-count">${c.count}</span>` : ''}
            ${check}
        </button>`;
    }).join('');
}

/**
 * Abre un popup con el listado completo de categorías y un buscador.
 * list: [{id, name, count?, icon?}], normalmente el mismo array pasado a chips().
 * onSelect(id) se llama al elegir una y el popup se cierra solo.
 */
let catPickerSeq = 0;

/**
 * Selector de categorías. Con `multi: true` se marcan varias (se aplican al instante y la lista
 * queda abierta hasta cerrarla); «Todo» vacía la selección; los elementos con `exclusive` (p. ej.
 * «Desbloquear») se comportan como una opción única. `active` es un id o, en multi, un array.
 */
export function openCategoryPicker({ title, list, active, onSelect, search, multi = false }) {
    const app = window.app;
    if (!app || !list.length) return null;
    const withSearch = search != null ? search : list.length > 8;
    let selected = Array.isArray(active) ? active.map(String) : (multi && active && active !== 'all' ? [String(active)] : []);
    const currentActive = () => (multi ? selected : active);
    // Framework7 ya deja p.el/p.$el vacíos para cuando dispara 'closed', así que no podemos
    // fiarnos de la instancia para localizar el nodo: le damos un id propio y lo buscamos
    // directamente en el DOM al cerrar (si no, se acumulan popups ocultos sin destruir).
    const popupId = 'catpick-' + (++catPickerSeq) + '-' + Date.now().toString(36);
    const html = `<div class="popup catpick-popup ${withSearch ? '' : 'is-compact'}" id="${popupId}">
        <div class="page">
            <div class="navbar">
                <div class="navbar-bg"></div>
                <div class="navbar-inner">
                    <div class="title">${escapeHtml(title)}</div>
                    <div class="right"><a href="#" class="link popup-close btn-icon" aria-label="${t('common.close')}"><span class="mi">close</span></a></div>
                </div>
            </div>
            <div class="page-content">
                ${withSearch ? `<div class="catpick-search"><span class="mi">search</span><input type="search" data-catpick-search placeholder="${escapeHtml(title)}…" autocomplete="off" autocorrect="off" spellcheck="false"/></div>` : ''}
                <div class="catpick-list" data-catpick-list>${categoryPickerItems(list, currentActive(), multi)}</div>
            </div>
        </div>
    </div>`;
    const popup = app.popup.create({
        content: html,
        on: {
            // Los listeners se enganchan en 'open' (inicio de la animación), no en 'opened':
            // si el usuario empieza a teclear antes de que termine la transición, ya filtra.
            open(p) {
                const el = p.$el[0];
                const listEl = el.querySelector('[data-catpick-list]');
                const searchEl = el.querySelector('[data-catpick-search]');
                const visible = () => {
                    const q = searchEl ? normalizeText(searchEl.value) : '';
                    return q ? list.filter((c) => normalizeText(c.name).indexOf(q) !== -1) : list;
                };
                const repaint = () => {
                    const filtered = visible();
                    listEl.innerHTML = filtered.length ? categoryPickerItems(filtered, currentActive(), multi) : empty({ title: t('common.noResults') });
                };
                listEl.addEventListener('click', (e) => {
                    const btn = e.target.closest('[data-catpick]');
                    if (!btn) return;
                    const id = btn.getAttribute('data-catpick');
                    const item = list.find((c) => String(c.id) === id);
                    if (!multi || (item && item.exclusive)) {
                        onSelect(id);
                        popup.close();
                        return;
                    }
                    if (id === 'all') selected = [];
                    else if (selected.indexOf(id) !== -1) selected = selected.filter((x) => x !== id);
                    else selected = selected.concat([id]);
                    onSelect(selected.slice());
                    repaint();
                    const again = listEl.querySelector(`[data-catpick="${CSS.escape(id)}"]`);
                    if (again && window.IPTV && window.IPTV.device.isTV) window.IPTV.tvnav.focus(again);
                });
                if (searchEl) searchEl.addEventListener('input', debounce(repaint, 150));
            },
            opened(p) {
                const el = p.$el[0];
                const searchEl = el.querySelector('[data-catpick-search]');
                if (window.IPTV && window.IPTV.device.isTV) setTimeout(() => window.IPTV.tvnav.focusLayer(el), 80);
                else if (searchEl && !window.IPTV.device.hasTouch) searchEl.focus();
            },
            closed() {
                try { popup.destroy(); } catch (e) { /* ignore */ }
                const el = document.getElementById(popupId);
                if (el && el.parentNode) el.parentNode.removeChild(el);
            }
        }
    });
    popup.open();
    return popup;
}

/**
 * Cabecera de página. Con `bar` (listas de canales, películas y series) en móvil es una barra fija: título a la
 * izquierda y a la derecha las acciones (p. ej. la guía) y el cambio de vista cuadrícula/lista. No lleva menú ni caja de
 * búsqueda: para eso están Inicio y la búsqueda general (pestaña Buscar). En pantalla ancha y en TV sigue siendo la
 * cabecera de siempre.
 */
export function pageHead({ title, search, searchPlaceholder, actions, bar = false }) {
    if (bar) {
        return `<div class="page-head page-head--bar" data-head>
        <h1 class="page-title">${escapeHtml(title)}</h1>
        <div class="head-actions">
            ${actions || ''}
            <a class="btn btn-icon head-mylist-btn" href="#" data-href="/mylist/" aria-label="${t('nav.mylist')}"><span class="mi">bookmark</span></a>
            ${search ? `<button type="button" class="btn btn-icon head-view-btn" data-view-toggle aria-label="${t('live.viewList')}"><span class="mi">view_list</span></button>` : ''}
        </div>
        ${search ? `<div class="searchbox head-search" style="max-width:420px"><span class="mi">search</span><input type="search" data-search placeholder="${escapeHtml(searchPlaceholder || '')}" autocomplete="off" autocorrect="off" spellcheck="false"/><button class="searchbox-clear" data-search-clear hidden aria-label="${t('common.close')}"><span class="mi">close</span></button><button type="button" class="searchbox-mic" data-voice hidden aria-label="${t('voice.label')}"><span class="mi">mic</span></button></div>` : ''}
    </div>`;
    }
    return `<div class="page-head">
        <h1 class="page-title">${escapeHtml(title)}</h1>
        <button type="button" class="btn btn-icon page-menu-btn nav-mobile-only" data-menu data-nav-skip aria-label="${t('nav.menu')}"><span class="mi">menu</span></button>
        ${search ? `<div class="searchbox" style="max-width:420px"><span class="mi">search</span><input type="search" data-search placeholder="${escapeHtml(searchPlaceholder || '')}" autocomplete="off" autocorrect="off" spellcheck="false"/><button class="searchbox-clear" data-search-clear hidden aria-label="${t('common.close')}"><span class="mi">close</span></button><button type="button" class="searchbox-mic" data-voice hidden aria-label="${t('voice.label')}"><span class="mi">mic</span></button></div>` : ''}
        ${actions || ''}
    </div>`;
}

/* ---------- Comportamientos sobre el DOM ---------- */

/** Botón de la cabecera (móvil) que cambia de vista (cuadrícula → por categorías → lista): acciona el selector de la barra. */
export function bindViewToggle(root) {
    const btn = root.querySelector('[data-view-toggle]');
    if (!btn) return;
    const ICON = { grid: 'grid_view', rows: 'view_stream', list: 'view_list', stage: 'smart_display' };
    const NAME = { grid: 'live.viewGrid', rows: 'live.viewRows', list: 'live.viewList', stage: 'live.viewStage' };
    const views = () => Array.from(root.querySelectorAll('.seg [data-view]'));
    const nextOf = () => {
        const all = views();
        const i = all.findIndex((b) => b.classList.contains('is-active'));
        return all[(i + 1) % all.length];
    };
    const sync = () => {
        const next = nextOf();
        if (!next) return;
        const v = next.getAttribute('data-view');
        btn.querySelector('.mi').textContent = ICON[v] || 'view_list';
        btn.setAttribute('aria-label', t(NAME[v] || 'live.viewList'));
    };
    btn.addEventListener('click', () => {
        const next = nextOf();
        if (next) next.click();
        setTimeout(sync, 0);
    });
    sync();
}


let imageObserver = null;

function ensureObserver() {
    if (imageObserver || !('IntersectionObserver' in window)) return imageObserver;
    imageObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const el = entry.target;
            imageObserver.unobserve(el);
            if (el.hasAttribute('data-poster-need')) needPoster(el);
            else loadImage(el);
        });
    }, { rootMargin: '300px 300px' });
    return imageObserver;
}

function loadImage(el) {
    const src = el.getAttribute('data-src');
    if (!src) return;
    el.removeAttribute('data-src');
    el.addEventListener('load', () => {
        el.classList.add('is-loaded');
        // El contenedor oculta su icono de relleno (tarjetas y filas de canal).
        if (el.parentElement) el.parentElement.classList.add('has-image');
    }, { once: true });
    el.addEventListener('error', () => {
        const box = el.parentElement;
        el.remove();
        // Carátula del proveedor que no carga: se prueba con la de TMDB.
        const card = box && box.closest && box.closest('[data-open]');
        if (!card || !box.matches('.card-poster, .mitem-poster')) return;
        const [kind, id] = String(card.getAttribute('data-open') || '').split(':');
        if (!kind || !id) return;
        box.setAttribute('data-poster-need', kind + ':' + id);
        posterFill.request(kind, id, { broken: true });
    }, { once: true });
    el.src = src;
}

/** Carga perezosa de imágenes con data-src dentro de root. */
export function lazyImages(root) {
    const imgs = root.querySelectorAll('img[data-src]');
    const obs = ensureObserver();
    imgs.forEach((el) => (obs ? obs.observe(el) : loadImage(el)));
    // Tarjetas sin carátula del proveedor: la de TMDB se pide cuando la tarjeta entra en pantalla.
    root.querySelectorAll('[data-poster-need]').forEach((el) => {
        if (el._needObserved) return;
        el._needObserved = true;
        if (obs) obs.observe(el); else needPoster(el);
    });
}

function needPoster(el) {
    const [kind, id] = String(el.getAttribute('data-poster-need') || '').split(':');
    if (kind && id) posterFill.request(kind, id);
}

// Llega la carátula de un título: se pone en todas sus tarjetas ya pintadas (con el mismo fundido que las demás).
posterFill.onPoster = (kind, id, poster) => {
    document.querySelectorAll(`[data-poster-need="${kind}:${id}"]`).forEach((box) => {
        box.removeAttribute('data-poster-need');
        if (box.querySelector('img')) return;
        const im = document.createElement('img');
        im.alt = '';
        im.decoding = 'async';
        im.setAttribute('data-src', imageUrl(poster, { card: true }));
        if (box.classList.contains('mitem-poster')) box.insertBefore(im, box.firstChild); else box.appendChild(im);
        loadImage(im);
    });
};

/** Flechas de desplazamiento en filas (escritorio). */
export function bindRowArrows(root) {
    root.querySelectorAll('.row').forEach((rowEl) => {
        if (rowEl._arrowsBound) return;
        rowEl._arrowsBound = true;
        const scroller = rowEl.querySelector('.row-scroller');
        // Scroll infinito horizontal: al acercarse al final de la fila se añade la siguiente tanda de esa categoría.
        const section = rowEl.closest('[data-row-id]');
        const feed = section ? rowFeeds.get(section.getAttribute('data-row-id')) : null;
        if (feed && scroller) {
            scroller.addEventListener('scroll', () => {
                if (feed.shown >= feed.items.length || scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - MORE_BEFORE_END_PX) return;
                scroller.insertAdjacentHTML('beforeend', takeMoreCards(feed));
                lazyImages(scroller);
            }, { passive: true });
        }
        rowEl.querySelectorAll('.row-arrow').forEach((btn) => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                const dir = btn.classList.contains('is-left') ? -1 : 1;
                scroller.scrollBy({ left: dir * scroller.clientWidth * 0.85, behavior: 'smooth' });
            });
        });
    });
}

/** Carga incremental: llama a onMore cuando el centinela entra en pantalla. */
export function infiniteScroll(sentinel, onMore) {
    if (!sentinel) return () => {};
    if (!('IntersectionObserver' in window)) {
        onMore();
        return () => {};
    }
    const obs = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) onMore();
    }, { rootMargin: '600px 0px' });
    obs.observe(sentinel);
    return () => obs.disconnect();
}

/** Actualiza un botón de favorito ya pintado. */
export function refreshFavButtons(root, kind, id) {
    const active = catalog.isFavorite(kind, id);
    root.querySelectorAll(`[data-fav="${kind}:${id}"]`).forEach((btn) => {
        btn.classList.toggle('is-active', active);
        const icon = btn.querySelector('.mi');
        // El corazón de la ficha (data-fav-heart) usa sus propios iconos.
        if (icon) icon.textContent = btn.hasAttribute('data-fav-heart') ? (active ? 'favorite' : 'favorite_border') : (active ? 'check' : 'add');
        btn.setAttribute('aria-label', active ? t('common.favoriteRemove') : t('common.favoriteAdd'));
    });
}

export function enhance(root) {
    lazyImages(root);
    bindRowArrows(root);
}
